import React from 'react';
import { AppInteractionLevel, AppMetadataProps, DEFAULT_APP_INTERACTION_LEVEL } from '@/core';
import { throwDevError } from '../../../core/utils/logging';

export interface AppMetadataProviderProps extends AppMetadataProps {
  children: React.ReactNode;
}

const AppMetadataContext = React.createContext<AppMetadataProps | undefined>(undefined);

/**
 * Context provider used to provide metadata about the application using the
 * visualization framework.
 *
 * Currently, this metadata is primarily used for attributing the source of
 * queries from the framework via HTTP headers and should be configured with
 * that use case in mind. It may be used for additional purposes in the future.
 */
export function AppMetadataProvider({ app, interactionLevel, metadata, children }: AppMetadataProviderProps) {
  const parentCtx = useAppMetadataContextInternal();

  const ctx = React.useMemo(() => {
    // Merge metadata with parent context
    const mergedMetadata = { ...parentCtx?.metadata, ...metadata };
    return {
      app: app ?? parentCtx?.app,
      interactionLevel: interactionLevel ?? parentCtx?.interactionLevel ?? DEFAULT_APP_INTERACTION_LEVEL,
      metadata: mergedMetadata,
    };
  }, [app, interactionLevel, metadata, parentCtx]);

  return <AppMetadataContext.Provider value={ctx}>{children}</AppMetadataContext.Provider>;
}

/**
 * Internal function that may be used in very specific cases where it's ok for the
 * resulting context to be undefined (e.g. in `AppMetadataProvider`, where it
 * tries to merge context from different levels). In most cases, `useAppMetadataContext`
 * should be used instead because it ensures that the context is configured.
 */
function useAppMetadataContextInternal() {
  return React.useContext(AppMetadataContext);
}

const COMMON_APP_METADATA_ERROR_MSG =
  'Set this context using `AppMetadataProvider` or `VisualizationProvider`, so we can accurately associate queries with the part of the application they came from. See https://viz-framework.chronosphere.io/?path=/docs/context-providers-appmetadataprovider--docs for more information.';

/**
 * useAppMetadataContext returns the current app metadata.
 */
export function useAppMetadataContext() {
  const ctx = useAppMetadataContextInternal();

  // Only throw errors in dev mode, so that engineers catch this when building
  // new features, but we do not accidentally break the page for customers if a
  // dev misses it.
  if (ctx === undefined) {
    throwDevError(`No AppMetadataContext found. ${COMMON_APP_METADATA_ERROR_MSG}`);
  }
  if (!ctx?.app) {
    throwDevError(`AppMetadataContext is missing the required \`app\` property. ${COMMON_APP_METADATA_ERROR_MSG}`);
  }

  return ctx;
}

/**
 * Returns the current interaction level allowed within the framework for this app. Useful for
 * tuning notices and other messaging to only provide guidance on editing panels/queries/etc. when
 * it is possible for the user to do so.
 *
 * This value is set by consumers via the `AppMetadataProvider`.
 */
export function useAppInteractionLevel(): AppInteractionLevel {
  const ctx = useAppMetadataContextInternal();

  return ctx?.interactionLevel || DEFAULT_APP_INTERACTION_LEVEL;
}
